import {ellipsis} from 'ellipsed';

import fadeIn from '../util/fade-in';
import fadeOut from '../util/fade-out';

import hideElement from '../util/hide-element';
import showElement from '../util/show-element';
import {register} from '../vendor/theme-scripts/theme-sections';
import Media from '../media/media';
import {productFormSection} from '../features/product-form';
import {swatchSection} from '../features/swatch';
import {tooltipSection} from '../features/tooltip';
import {popoutSection} from '../features/popout';
import accordions from '../features/accordion';
import QuantityCounter from '../globals/quantity-handle';
import copyClipboard from '../features/copy-clipboard';
import tabs from '../features/tabs';

window.theme.variables = {
  productPageSticky: false,
  bpSmall: false,
};

const selectors = {
  addToCart: '[data-add-to-cart]',
  priceWrapper: '[data-price-wrapper]',
  slideshow: '[data-product-slideshow]',
  productImage: '[data-product-image]',
  productJson: '[data-product-json]',
  form: '[data-product-form]',
  thumbs: '[data-product-thumbs]',
  dataSectionId: 'data-section-id',
  dataTallLayout: 'data-tall-layout',
  dataStickyEnabled: 'data-sticky-enabled',
  dataCartBar: 'data-cart-bar',
  dataReviews: 'data-reviews',
  dataProductShare: '[data-product-share]',
  dataProductShareValue: 'data-product-share',
  dataProductShareTitleValue: 'data-product-share-title',
  productPage: '.product__page',
  formWrapper: '.form__wrapper',
  cartBar: '#cart-bar',
  productSubmitAdd: '.product__submit__add',
  cartBarAdd: 'data-add-to-cart-bar',
  cartBarScroll: 'data-cart-bar-scroll',
  templateProduct: '#template-product',
  siteFooterWrapper: '.site-footer-wrapper',
  shopifyProductReviews: '#shopify-product-reviews',
  toggleTruncateHolder: '[data-truncated-holder]',
  toggleTruncateButton: '[data-truncated-button]',
  toggleTruncateContent: '[data-truncated-content]',
  toggleTruncateContentAttr: 'data-truncated-content',
  headerSticky: '[data-header-sticky="sticky"]',
  upsellButton: '[data-upsell-btn]',
  upsellButtonText: '[data-upsell-btn-text]',
  scrollToElement: '[data-scroll-to]',
  scrollToElementValue: 'data-scroll-to',
  accordionHolder: '[data-accordion-holder]',
  accordionToggle: '[data-accordion-toggle]',
  headerHeight: '[data-header-height]',
  dataModalButton: 'data-product-popup',
  formWrapper: '[data-form-wrapper]',
  tooltip: '[data-tooltip]',
  tooltipStopMousenterValue: 'data-tooltip-stop-mouseenter',
};

const classes = {
  classExpanded: 'is-expanded',
  classSticky: 'is-sticky',
  classStickyHeader: 'with-sticky-header',
  classVisible: 'is-visible',
  classLoading: 'is-loading',
  classSiteFooterPush: 'site-footer--push',
  open: 'is-open',
  hasPopup: 'has-popup',
};

const sections = {};

/**
 * Product section constructor.
 * @param {string} container - selector for the section container DOM element
 */
class Product {
  constructor(section) {
    this.section = section;
    this.container = section.container;
    this.id = this.container.getAttribute(selectors.dataSectionId);
    this.tallLayout = this.container.getAttribute(selectors.dataTallLayout) === 'true';
    this.stickyEnabled = this.container.getAttribute(selectors.dataStickyEnabled) === 'true';
    this.headerSticky = document.querySelector(selectors.headerSticky) !== null;
    this.showReviews = this.container.getAttribute(selectors.dataReviews) === 'true';
    this.thumbs = this.container.querySelector(selectors.thumbs);
    this.shareButton = this.container.querySelector(selectors.dataProductShare);
    this.upsellButton = this.container.querySelector(selectors.upsellButton);
    this.scrollToButton = this.container.querySelector(selectors.scrollToElement);
    this.truncateElementHolder = this.container.querySelector(selectors.toggleTruncateHolder);
    this.truncateElement = this.container.querySelector(selectors.toggleTruncateContent);
    this.modalButton = this.container.querySelectorAll(`[${selectors.dataModalButton}]`);
    this.formWrapper = this.container.querySelector(selectors.formWrapper);
    this.resizeEventTruncate = () => this.truncateText();
    this.resizeEventSticky = () => this.stickyScrollCheck();
    this.resizeEventUpsell = () => this.calcUpsellButtonDemensions();
    this.scrollEvent = () => this.scrollTop();

    if (this.scrollToButton) {
      this.scrollToReviews();
    }

    this.shareToggle();

    if (this.truncateElementHolder && this.truncateElement) {
      setTimeout(this.resizeEventTruncate, 50);
      document.addEventListener('theme:resize', this.resizeEventTruncate);
    }

    // Stop parsing if we don't have the product json script tag when loading
    // section in the Theme Editor
    const productJson = this.container.querySelector(selectors.productJson);
    if ((productJson && !productJson.innerHTML) || !productJson) {
      const counter = new QuantityCounter(this.container);
      counter.init();
      return;
    }

    this.form = this.container.querySelector(selectors.form);

    this.init();

    if (this.stickyEnabled) {
      this.stickyScroll();
    }

    if (this.container.getAttribute(selectors.dataCartBar) === 'true') {
      this.initCartBar();
    }

    const reviewsAppInstalled = typeof window.SPR == 'function';

    if (this.showReviews && reviewsAppInstalled) {
      window.SPR.initDomEls();
      window.SPR.loadBadges();
    }

    if (this.upsellButton) {
      this.upsellButtonDemensions();
    }

    if (this.modalButton.length > 0) {
      this.productPopup();
    }
  }

  init() {
    theme.mediaInstances[this.id] = new Media(this.section);
    theme.mediaInstances[this.id].init();
  }

  productPopup() {
    this.modalButton.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const modal = document.querySelector(`#${button.getAttribute(selectors.dataModalButton)}`);

        if (window.getComputedStyle(modal).display !== 'none') {
          fadeOut(modal);
          this.formWrapper.classList.remove(classes.hasPopup);
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
        }

        if (window.getComputedStyle(modal).display === 'none') {
          fadeIn(modal);
          this.formWrapper.classList.add(classes.hasPopup);
          document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: modal}));
        }
      });
    });
  }

  upsellButtonDemensions() {
    this.calcUpsellButtonDemensions();

    document.addEventListener('theme:resize', this.resizeEventUpsell);
  }

  calcUpsellButtonDemensions() {
    const upsellButtonText = this.upsellButton.querySelector(selectors.upsellButtonText);
    if (upsellButtonText) {
      this.upsellButton.style.setProperty('--btn-text-width', `${upsellButtonText.clientWidth}px`);
    }
  }

  stickyScroll() {
    this.stickyScrollCheck();

    document.addEventListener('theme:resize', this.resizeEventSticky);
  }

  stickyScrollCheck() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const isDesktop = windowWidth >= window.theme.sizes.small;
    const form = this.container.querySelector(selectors.formWrapper);
    const targetFormWrapper = this.container.querySelector(`${selectors.productPage} ${selectors.formWrapper}`);

    if (isDesktop) {
      const slideshow = this.container.querySelector(selectors.slideshow);
      if (!form || !slideshow) return;
      const productCopyHeight = form.offsetHeight;
      const productImagesHeight = slideshow.offsetHeight;

      // Is the product description and form taller than window space
      // Is is also shorter than the window and images
      if (productCopyHeight < productImagesHeight || productCopyHeight < window.innerHeight) {
        theme.variables.productPageSticky = true;

        targetFormWrapper.classList.add(classes.classSticky);
        if (this.headerSticky) {
          targetFormWrapper.classList.add(classes.classStickyHeader);
        }
      } else {
        theme.variables.productPageSticky = false;
        targetFormWrapper.classList.remove(classes.classSticky);
      }
    } else {
      targetFormWrapper.classList.remove(classes.classSticky);
    }
  }

  truncateText() {
    if (this.truncateElementHolder.classList.contains(classes.classVisible)) return;
    const truncateRows = 5;
    const truncateElementCloned = this.truncateElement.cloneNode(true);
    const truncateElementClass = this.truncateElement.getAttribute(selectors.toggleTruncateContentAttr);
    const truncateNextElement = this.truncateElement.nextElementSibling;
    if (truncateNextElement) {
      truncateNextElement.remove();
    }

    this.truncateElement.parentElement.append(truncateElementCloned);

    const truncateAppendedElement = this.truncateElement.nextElementSibling;
    truncateAppendedElement.classList.add(truncateElementClass);
    truncateAppendedElement.removeAttribute(selectors.toggleTruncateContentAttr);

    showElement(truncateAppendedElement);

    ellipsis(truncateAppendedElement, truncateRows, {
      replaceStr: '',
    });

    hideElement(truncateAppendedElement);

    if (this.truncateElement.innerHTML !== truncateAppendedElement.innerHTML) {
      this.truncateElementHolder.classList.add(classes.classExpanded);
    } else {
      truncateAppendedElement.remove();
      this.truncateElementHolder.classList.remove(classes.classExpanded);
    }

    this.toggleTruncatedContent(this.truncateElementHolder);
  }

  toggleTruncatedContent(holder) {
    const toggleButton = holder.querySelector(selectors.toggleTruncateButton);
    if (toggleButton) {
      toggleButton.addEventListener('click', (e) => {
        e.preventDefault();
        holder.classList.remove(classes.classExpanded);
        holder.classList.add(classes.classVisible);
      });
    }
  }

  shareToggle() {
    if (this.shareButton) {
      this.shareButton.addEventListener('click', function () {
        const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

        if (navigator.share && windowWidth <= 1024) {
          const shareTitle = this.hasAttribute(selectors.dataProductShareTitleValue) ? this.getAttribute(selectors.dataProductShareTitleValue) : window.location.hostname;
          const shareUrl = this.hasAttribute(selectors.dataProductShareValue) ? this.getAttribute(selectors.dataProductShareValue) : window.location.href;

          navigator
            .share({
              title: shareTitle,
              url: shareUrl,
            })
            .then(() => {
              console.log('Thanks for sharing!');
            })
            .catch(console.error);
        } else {
          this.parentElement.classList.toggle(classes.classExpanded);
        }
      });
    }
  }

  initCartBar() {
    const cartBar = document.querySelector(selectors.cartBar);
    const cartBarBtn = cartBar.querySelector(selectors.productSubmitAdd);

    // Submit product form on cart bar button click
    if (cartBarBtn) {
      cartBarBtn.addEventListener('click', (e) => {
        e.preventDefault();

        if (e.target.hasAttribute(selectors.cartBarAdd)) {
          if (theme.cartDrawerEnabled) {
            e.target.classList.add(classes.classLoading);
            e.target.setAttribute('disabled', 'disabled');
          }

          this.form.querySelector(selectors.addToCart).dispatchEvent(
            new Event('click', {
              bubbles: true,
            })
          );
        } else if (e.target.hasAttribute(selectors.cartBarScroll)) {
          this.scrollToTop();
        }
      });

      if (cartBarBtn.hasAttribute(selectors.cartBarAdd)) {
        document.addEventListener('product:bar:error', () => this.scrollToTop());
      }

      if (theme.cartDrawerEnabled) {
        document.addEventListener('product:bar:button', () => {
          if (cartBarBtn && cartBarBtn.classList.contains(classes.classLoading)) {
            cartBarBtn.classList.remove(classes.classLoading);
            cartBarBtn.removeAttribute('disabled');
          }
        });
      }
    }

    this.cartBar = cartBar;

    document.addEventListener('theme:scroll', this.scrollEvent);
  }

  scrollToTop() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const isDesktop = windowWidth >= window.theme.sizes.small;
    const scrollTarget = isDesktop ? this.container : this.form;

    window.scrollTo({
      top: scrollTarget.getBoundingClientRect().top + window.scrollY,
      left: 0,
      behavior: 'smooth',
    });
  }

  scrollTop() {
    const scrolled = window.pageYOffset;
    const siteFooter = document.querySelector(selectors.siteFooterWrapper);

    if (this.form && this.cartBar) {
      const formOffset = this.form.offsetTop;
      const formHeight = this.form.offsetHeight;
      const checkPosition = scrolled > formOffset + formHeight;

      this.cartBar.classList.toggle(classes.classVisible, checkPosition);
      siteFooter.classList.toggle(classes.classSiteFooterPush, checkPosition);

      siteFooter.style.marginBottom = siteFooter.classList.contains(classes.classSiteFooterPush) ? `${this.cartBar.offsetHeight}px` : '0';
    }
  }

  scrollToReviews() {
    this.scrollToButton.addEventListener('click', (e) => {
      e.preventDefault();

      const element = document.querySelector(this.scrollToButton.getAttribute(selectors.scrollToElementValue));

      if (!element) {
        return;
      }

      const accordionToggle = element.querySelector(selectors.accordionToggle);
      const accordionHolder = accordionToggle.closest(selectors.accordionHolder);
      let timeoutFlag = false;

      if (!accordionToggle.classList.contains(classes.open) && accordionHolder && accordionHolder.querySelector(`${selectors.accordionToggle}.${classes.open}`)) {
        timeoutFlag = true;
      }

      if (!accordionToggle.classList.contains(classes.open)) {
        accordionToggle.dispatchEvent(new Event('click'));
      }

      if (timeoutFlag) {
        setTimeout(() => this.scrollToElement(element), 500);
      } else {
        this.scrollToElement(element);
      }
    });
  }

  scrollToElement(element) {
    const headerHeight = this.headerSticky ? document.querySelector(selectors.headerHeight).getBoundingClientRect().height : 0;

    window.scrollTo({
      top: element.getBoundingClientRect().top + window.scrollY - headerHeight,
      left: 0,
      behavior: 'smooth',
    });

    const tooltips = document.querySelectorAll(`${selectors.tooltip}:not([${selectors.tooltipStopMousenterValue}])`);
    if (tooltips.length) {
      tooltips.forEach((tooltip) => {
        tooltip.setAttribute(selectors.tooltipStopMousenterValue, '');

        setTimeout(() => {
          tooltip.removeAttribute(selectors.tooltipStopMousenterValue);
        }, 1000);
      });
    }
  }

  onUnload() {
    if (this.truncateElementHolder && this.truncateElement) {
      document.removeEventListener('theme:resize', this.resizeEventTruncate);
    }

    if (this.stickyEnabled) {
      document.removeEventListener('theme:resize', this.resizeEventSticky);
    }

    if (this.upsellButton) {
      document.removeEventListener('theme:resize', this.resizeEventUpsell);
    }

    if (this.container.getAttribute(selectors.dataCartBar) === 'true') {
      document.removeEventListener('theme:scroll', this.scrollEvent);
    }
  }
}

const productSection = {
  onLoad() {
    sections[this.id] = new Product(this);
  },
  onUnload(e) {
    sections[this.id].onUnload(e);
  },
};

register('product', [productFormSection, productSection, swatchSection, tooltipSection, popoutSection, tabs, accordions, copyClipboard]);
